import React from 'react';
import styled from 'styled-components';
import { AttributionText, Box, CTALink, Heading, Inline, Link, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors } from '@99designs/emu/settings';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { IconContacts1, IconPhone1 } from '@99designs/icons';
import { ZendeskLanguage, linkTo } from '@99designs/routing';
import { Site } from '@99designs/site-navigation/lib/Site';
import { ReactComponent as GiftLady } from '../../images/gift-lady.svg';
import { ReactComponent as ScooterDudeIcon } from '../../images/scooter-dude.svg';

const StyledHeading = styled(Heading)`
    color: ${(props) => (props.color ? props.color : colors.grey800)};
`;

const GiftLadyWrapper = styled.div`
    position: absolute;
    width: 350px;
    left: -360px;
    top: -180px;
    transform: rotateZ(-12deg);

    ${mediaQuery.between('small', 'medium')} {
        display: none;
    }
`;

const ScooterDudeWrapper = styled.div`
    position: absolute;
    top: -340px;
    right: -481px;
    width: 400px;

    ${mediaQuery.between('small', 'medium')} {
        display: none;
    }
`;

const ScooterDude = styled(ScooterDudeIcon)`
    transform: scaleX(-1) rotateZ(-16deg);
`;

const AttributionWrapper = styled.div`
    position: absolute;
    left: -50px;
`;

interface SupportProps {
    site: Site;
}

export function Support({ site }: SupportProps) {
    const supportPhoneNumber = site.siteConfig['phone.support'];

    return (
        <>
            <StyledHeading color="risingSun" accessibilityLevel={2} appearanceLevel={2}>
                {__("Questions? We've got the answers")}
            </StyledHeading>
            <Box marginTop={'2bu'}>
                <Text as="p" color="grey700">
                    <FormattedMessage
                        message={__(
                            'Our kind, happy and humble customer support team would love to answer your questions. {sendUsAnEmailLink} or call to speak with an actual human.'
                        )}
                        values={{
                            sendUsAnEmailLink: (
                                <Link
                                    color={colors.grey700}
                                    href={linkTo.supportContactUrl(
                                        site.getLanguageCode() as ZendeskLanguage
                                    )}
                                >
                                    {__('Send us an email')}
                                </Link>
                            ),
                        }}
                    />
                </Text>
                <Box
                    display={'flex'}
                    flexDirection={'column'}
                    marginTop={'2bu'}
                    style={{ position: 'relative' }}
                >
                    <GiftLadyWrapper>
                        <GiftLady />
                    </GiftLadyWrapper>
                    <Inline space="0.75bu">
                        <IconPhone1 color={colors.risingSun} />
                        <CTALink href={`tel:${supportPhoneNumber}`} color={colors.risingSun}>
                            {supportPhoneNumber}
                        </CTALink>
                    </Inline>
                    <Box marginTop="0.25bu" />
                    <Inline space="0.75bu">
                        <IconContacts1 color={colors.risingSun} />
                        <CTALink href="/design-consultation" color={colors.risingSun}>
                            {__('Free design consultation')}
                        </CTALink>
                    </Inline>
                    <ScooterDudeWrapper>
                        <ScooterDude />
                        <AttributionWrapper>
                            <AttributionText>
                                <FormattedMessage
                                    message={__('illustrations by {designerName}')}
                                    values={{ designerName: 'wildanya' }}
                                />
                            </AttributionText>
                        </AttributionWrapper>
                    </ScooterDudeWrapper>
                </Box>
            </Box>
        </>
    );
}
